import { Component, Host, h, Prop, Event, EventEmitter, State, Listen, Watch } from '@stencil/core';
import { SVGIcon } from '../icons/svg-icon';
import classnames from 'classnames';
import { APP_DIRECTORY_OPTION, DASHBOARD_OPTION } from '../../../utils';

const MENU_MAX_ITEMS_IN_VIEW = 10;
const SUBMENU_MAX_ITEMS_IN_VIEW = 10;

export type DropdownItemType = "organization" | "product" | "instance" | "link" | "button";

export interface DropdownItem {
  heading?: boolean;
  external?: boolean;
  value?: string | number;
  valueObject?: any;
  subItems?: any[];
  href?: string;
  selected?: boolean;
  text: string;
  onClick?: void;
  description?: string;
  leftIcon?: 'arrow-right-from-bracket' | 'arrow-up-right-from-square' | 'chevron-right' | 'magnifying-glass' | 'circle-question' | 'user';
  rightIcon?: string;
  type?: DropdownItemType
}

@Component({
  tag: 'dropdown-list',
  styleUrl: 'dropdown-list.scss',
  shadow: true,
})
export class DropdownList {
  @Prop()
  listId: string;
  @Prop()
  class: string = '';

  @Prop({ attribute: 'selected' })
  selected?: (string | number)[];

  @Prop({ mutable: true })
  items: DropdownItem[] = [];

  @Watch('items')
  handleOptionsChanged(newItems: any) {
    this.items = newItems;
  }

  @Prop()
  menuMaxItemsInView: number = MENU_MAX_ITEMS_IN_VIEW;

  @Prop()
  subMenuMaxItemsInView: number = SUBMENU_MAX_ITEMS_IN_VIEW;

  @Prop()
  subMenuClass: string = '';

  @Prop()
  disableFilter: boolean = false;

  @Prop()
  filterPlaceholder: string = 'Filter...';

  @Prop()
  styles: { [key: string]: string } = {};

  @Event({
    eventName: 'opti-dropdown-list-selection',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
  select: EventEmitter<DropdownItem>;

  @State()
  filterQuery: string = '';

  @Listen('opti-dropdown-query-updated')
  filterUpdatedHandler(event) {
    if (event.detail.parentId === this.listId) {
      this.filterQuery = event.detail.query || '';
    }
  }

  handleItemClick = ({ clickEvent, item }) => {
    if (['organization', 'product', 'instance'].includes(item.type)) {
      clickEvent.preventDefault();
    }

    item.onClick && item.onClick();

    if (!item.subItems?.length) {
      //emit item if there are no subItems to select
      this.select.emit(item);
    } else if (item.subItems?.length === 1) {
      //emit the first subItem automatically when product with single instance selected
      this.select.emit(item.subItems[0]);
    }
  };

  _isFilterMatch(label: string, heading: boolean, query: string) {
    if (heading) return true;
    if (label && query) {
      return label.toLowerCase().includes(query.toLowerCase());
    } else {
      return true;
    }
  }

  render() {
    const filteredItems = this.items.filter(item => this._isFilterMatch(item.text, item.heading, this.filterQuery));
    let menuStyles: { [key: string]: string } = {};

    if (this.menuMaxItemsInView < MENU_MAX_ITEMS_IN_VIEW) {
      menuStyles.overflowY = 'scroll';
      menuStyles.maxHeight = `calc(40px * ${this.subMenuMaxItemsInView})`;
    }

    const showFilter = !this.disableFilter && this.items.length > this.menuMaxItemsInView;
    const noResults = this.items.length > 0 && filteredItems.filter(item => !item.heading).length === 0;

    return (
      <Host style={menuStyles}>
        <ul>
          {showFilter && (
            <li>
              <filter-input placeholder={this.filterPlaceholder} parentId={this.listId} />
            </li>
          )}
          {noResults ? (
            <li class="dropdown-list__no-results">No results.</li>
          ) : (
            filteredItems.map((item, itemIndex) => {
              const { heading, subItems, text, href, external, value, description, leftIcon, rightIcon } = item;
              const hasSubItems = subItems?.length > 0;
              const showSubItems = subItems?.length > 1;

              let subMenuStyles: { [key: string]: string } = {};

              if (this.subMenuMaxItemsInView < SUBMENU_MAX_ITEMS_IN_VIEW) {
                subMenuStyles.overflowY = 'scroll';
                subMenuStyles.maxHeight = `calc(40px * ${this.subMenuMaxItemsInView})`;
              }

              if (heading) {
                return (
                  <li class="dropdown-list__name" title={text}>
                    {text}
                  </li>
                );
              }

              if (description) {
                return (
                  <li class="dropdown-list__link-with-description">
                    <div>{text}</div>
                    <div>{description}</div>
                  </li>
                );
              }

              if (!showSubItems && value && this.selected?.indexOf(value) > -1) {
                return (
                  <li title={text} class="dropdown-list__item dropdown-list__item--selected">
                    <span class="dropdown-list__item-text">
                      {text}
                    </span>
                    {rightIcon && <SVGIcon name={rightIcon} />}

                  </li>
                );
              }
              const dropdownClasses = classnames('dropdown-list__item', {
                'dropdown-list__item--overflow': showSubItems,
              });

              const TargetTag = !!href && !showSubItems && href !== DASHBOARD_OPTION.href && href !== APP_DIRECTORY_OPTION.href ? 'a' : 'button';
              const tagProps: any = {
                class: 'dropdown-list__item-link',
                title: text,
              };

              if (href && !hasSubItems && href !== DASHBOARD_OPTION.href && href !== APP_DIRECTORY_OPTION.href) {
                tagProps.href = href;
                tagProps.target = external ? '_blank' : '_self';
              }

              return (
                <li class={dropdownClasses} tabIndex={itemIndex + 1}>
                  <TargetTag {...tagProps} onClick={clickEvent => this.handleItemClick({ clickEvent, item })}>
                    {leftIcon && <SVGIcon name={leftIcon}></SVGIcon>}

                    <span class="dropdown-list__item-link--truncated">{text}</span>
                    {rightIcon && <SVGIcon name={rightIcon} />}
                    {external && <SVGIcon name="arrow-up-right-from-square"></SVGIcon>}
                    {showSubItems && <SVGIcon name="chevron-right" width={10}></SVGIcon>}
                  </TargetTag>
                  {showSubItems && (
                    <dropdown-list
                      class="dropdown-list__submenu"
                      listId={item.value.toString()}
                      selected={this.selected}
                      style={subMenuStyles}
                      items={[{ text: `${item.text} Instances`, heading: true }, ...item.subItems]}
                      subMenuMaxItemsInView={this.subMenuMaxItemsInView}
                    ></dropdown-list>
                  )}
                </li>
              );
            })
          )}
        </ul>
      </Host>
    );
  }
}
